import { cn } from '@/utils/helpers/classnames';
import React, { useMemo } from 'react';
import Select, { type MultiValue, type SingleValue, type StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  flagUrl?: string;
}

interface CustomSelectProps {
  id?: string;
  name?: string;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  isMulti?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  isCreatable?: boolean;
  multiValueLayout?: 'grow' | 'scroll';
  onBlur?: () => void;
  required?: boolean;
  error?: string;
  label?: string | React.ReactNode;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  id,
  name,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  isMulti = false,
  isSearchable = true,
  isClearable = false,
  isCreatable = false,
  multiValueLayout = 'grow',
  onBlur,
  required = false,
  error,
  label,
}) => {
  const SelectComponent = isCreatable ? CreatableSelect : Select;

  const selectedValue = useMemo(() => {
    if (isMulti) {
      const selectedValues = Array.isArray(value)
        ? value
        : value
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);

      return selectedValues.map((selectedTag) => {
        const matched = options.find((option) => option.value === selectedTag);
        return matched ?? { value: selectedTag, label: selectedTag };
      });
    }

    const singleValue = Array.isArray(value) ? value[0] || '' : value;
    const matched = options.find((option) => option.value === singleValue);
    return matched ?? (singleValue ? { value: singleValue, label: singleValue } : null);
  }, [isMulti, options, value]);

  const customStyles = useMemo<StylesConfig<SelectOption, boolean>>(() => ({
    control: (base, state) => ({
      ...base,
      minHeight: '2.75rem',
      ...(isMulti && multiValueLayout === 'scroll'
        ? {
          height: '2.75rem',
          maxHeight: '2.75rem',
          overflow: 'hidden',
          alignItems: 'flex-start',
        }
        : {}),
      borderColor: state.isFocused ? '#9CA3AF' : error ? '#EB5757' : '#D1D5DB',
      boxShadow: 'none',
      borderRadius: '0.5rem',
      '&:hover': {
        borderColor: '#9CA3AF',
      },
      backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
      opacity: disabled ? 0.7 : 1,
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '2px 10px',
      ...(isMulti && multiValueLayout === 'scroll'
        ? {
          maxHeight: '2.5rem',
          overflowY: 'auto',
          overflowX: 'hidden',
          alignContent: 'flex-start',
        }
        : {}),
    }),
    menu: (base) => ({
      ...base,
      zIndex: 100000,
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#681628' : state.isFocused ? '#F7ECEF' : '#FFFFFF',
      color: state.isSelected ? '#FFFFFF' : '#111827',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      ':active': {
        backgroundColor: '#681628',
        color: '#FFFFFF',
      },
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#F1F5F9',
      borderRadius: '9999px',
      paddingLeft: '4px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#334155',
      fontSize: '0.75rem',
      fontWeight: 600,
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#475569',
      borderRadius: '9999px',
      ':hover': {
        backgroundColor: '#E2E8F0',
        color: '#0F172A',
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6B7280',
      fontSize: '0.875rem',
    }),
    indicatorSeparator: (base) => ({
      ...base,
      display: 'none',
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: 0,
    }),
  }), [disabled, error, isMulti, multiValueLayout]);

  const handleChange = (newValue: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
    if (isMulti) {
      const nextValues = (newValue as MultiValue<SelectOption>).map((option) => option.value);
      onChange(nextValues);
      return;
    }

    const nextValue = (newValue as SingleValue<SelectOption>)?.value ?? '';
    onChange(nextValue);
  };

  const handleCreateOption = (inputValue: string) => {
    const createdValue = inputValue.trim();
    if (!createdValue) {
      return;
    }

    if (isMulti) {
      const previousValues = Array.isArray(value)
        ? value
        : value
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);

      if (!previousValues.includes(createdValue)) {
        onChange([...previousValues, createdValue]);
      }
      return;
    }

    onChange(createdValue);
  };

  const formatOptionLabel = (option: SelectOption) => {
    if (!option.flagUrl) {
      return option.label;
    }

    return (
      <div className="flex items-center gap-2">
        <img src={option.flagUrl} alt="" className="h-4 w-5 rounded-sm object-cover" loading="lazy" aria-hidden="true" />
        <span>{option.label}</span>
      </div>
    );
  };

  return (
    <div className={cn('relative flex flex-col', className)}>

      {/* Label */}
      <div className="mb-1">
            {label && typeof label === 'string' ? (
        <label htmlFor={id} className={cn('text-sm font-medium', required ? 'after:content-["*"] after:ml-0.5 after:text-red-500' : '')}>
          {label}
        </label>
      ) : (
        label
      )}
      </div>
  
      <SelectComponent
        inputId={id}
        name={name}
        value={selectedValue}
        options={options}
        onChange={handleChange}
        onBlur={onBlur}
        required={required}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        onCreateOption={isCreatable ? handleCreateOption : undefined}
        placeholder={placeholder}
        isDisabled={disabled}
        isMulti={isMulti}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isOptionDisabled={(option) => Boolean(option.disabled)}
        formatOptionLabel={formatOptionLabel}
        classNamePrefix="custom-select"
        styles={customStyles}
        noOptionsMessage={() => 'No options found'}
        menuPortalTarget={typeof window !== 'undefined' ? window.document.body : null}
        menuPosition="fixed"
      />
      {error && (
        <p id={`${id}-error`} className="text-xs absolute -bottom-4 text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default CustomSelect;

